import React, { useCallback } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import { useFormikContext } from 'formik';
import { startCase, isEmpty } from 'lodash-es';
import {
  ButtonIconLocation,
  IconLookup,
  Tooltip,
} from 'component-library-and-tools';

import { colors } from 'lib/prisma-styles';

import Button from '../../../components/Button';

import { BackButtonRenderer, NextButtonRenderer, SubmitButtonRenderer } from '../types';

import { StepProps } from './types';

const BUTTON_ICON_CLASS_NAME = 'step__button-icon';
const NEXT_BUTTON_ICON_CLASS_NAME = 'step__next-button-icon';
const BACK_BUTTON_ICON_CLASS_NAME = 'step__back-button-icon';

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.333333333rem 2.5rem;
  overflow: hidden;
`;

const StyledHeaderContainer = styled.div`
  margin-bottom: 3.083333333rem;
`;

const StyledTitle = styled.h1`
  margin: 0 0 0.333333333rem;
  font-size: 2.333333333rem;
`;

const StyledSubtitle = styled.p`
  margin: 0;
  font-size: 1.166666667rem;
`;

const StyledContentContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 1.333333333rem;
  min-height: 0;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledButton = styled(Button)`
  && {
    min-height: 3.666666667rem;

    .${BUTTON_ICON_CLASS_NAME} {
      font-weight: 700;
      font-size: 1.666666667rem;
    }
  }
`;

const StyledNextButton = styled(StyledButton)`
  && {
    .${NEXT_BUTTON_ICON_CLASS_NAME} {
      margin: 0 0 0 0.666666667rem;
    }
  }
`;

const StyledBackButton = styled(StyledButton)`
  && {
    margin-right: 2rem;
    background-color: transparent;
    color: ${colors.content.default};
    border-radius: unset;

    .${BACK_BUTTON_ICON_CLASS_NAME} {
      margin: 0 0.666666667rem 0 0;
    }
  }
`;

const renderDefaultBackButton: BackButtonRenderer = ({ label, onClick }) => (
  <StyledBackButton
    iconClassName={classnames(BUTTON_ICON_CLASS_NAME, BACK_BUTTON_ICON_CLASS_NAME)}
    icon={IconLookup.ARROW_LEFT}
    label={label!}
    onClick={onClick}
  />
);

const renderDefaultNextButton: NextButtonRenderer = ({ stepErrors, label, onClick }) => (
  <Tooltip
    color='#33373d'
    content={stepErrors}
    preventFromOpening={isEmpty(stepErrors)}
    limitWidth={180}
  >
    <StyledNextButton
      iconClassName={classnames(BUTTON_ICON_CLASS_NAME, NEXT_BUTTON_ICON_CLASS_NAME)}
      icon={IconLookup.ARROW_RIGHT}
      iconLocation={ButtonIconLocation.RIGHT}
      isDisabled={!isEmpty(stepErrors)}
      label={label!}
      onClick={onClick}
    />
  </Tooltip>
);

const renderDefaultSubmitButton: SubmitButtonRenderer = ({
  errors,
  stepErrors,
  label,
  onClick,
  isLoading,
}) => (
  <Tooltip
    color='#33373d'
    content={stepErrors || 'There are some errors in the form that need to be fixed before submitting it'}
    preventFromOpening={isEmpty(stepErrors) && isEmpty(errors)}
    limitWidth={180}
  >
    <StyledButton
      isDisabled={!isEmpty(stepErrors) || !isEmpty(errors)}
      label={label!}
      onClick={onClick}
      isLoading={isLoading}
    />
  </Tooltip>
);

const Step = <T extends unknown>({
  currentStep,
  currentStepIndex,
  numberOfSteps,
  stepValues,
  errors,
  touched,
  onBackButtonClick,
  onNextButtonClick,
  defaultBackButtonLabel = 'Back',
  defaultNextButtonLabel = 'Next',
  submitButtonLabel = 'Submit',
  renderBackButton = renderDefaultBackButton,
  renderNextButton = renderDefaultNextButton,
  renderSubmitButton = renderDefaultSubmitButton,
  showNextButton = true,
  showBackButton = true,
  buttonContainerClassName,
  isSubmitting,
}: StepProps<T>) => {
  const { setFieldValue, setFieldTouched, submitForm } = useFormikContext();
  const {
    name,
    title,
    subtitle,
    component: Component,
    nextButtonLabel,
    backButtonLabel,
  } = currentStep;
  const titleToRender = title || name;
  const stepErrors = errors[name];
  const isLastStep = currentStepIndex === numberOfSteps - 1;

  const handleChange = useCallback((value, fieldName) => {
    const HAS_BEEN_TOUCHED = true;
    const RUN_VALIDATOR_ON_UPDATE = true;
    const fieldToUpdate = fieldName ? `${name}.${fieldName}` : name;

    setFieldValue(fieldToUpdate, value);
    setFieldTouched(fieldToUpdate, HAS_BEEN_TOUCHED, !RUN_VALIDATOR_ON_UPDATE);
  }, [currentStep]);

  const handleBackButtonClick = useCallback(() => {
    onBackButtonClick(currentStepIndex - 1);
  }, [currentStepIndex]);

  const handleNextButtonClick = useCallback(() => {
    onNextButtonClick(currentStepIndex + 1);
  }, [currentStepIndex]);

  return (
    <StyledContainer>
      {titleToRender && (
        <StyledHeaderContainer>
          <StyledTitle>{startCase(titleToRender)}</StyledTitle>
          {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
        </StyledHeaderContainer>
      )}
      <StyledContentContainer>
        <Component
          onChange={handleChange}
          values={stepValues}
          errors={stepErrors}
          touched={touched}
        />
      </StyledContentContainer>
      <StyledButtonContainer className={buttonContainerClassName}>
        {showBackButton && currentStepIndex > 0 && renderBackButton({
          label: backButtonLabel || defaultBackButtonLabel,
          onClick: handleBackButtonClick,
        })}
        {!isLastStep && showNextButton
          ? renderNextButton({
              stepErrors,
              label: nextButtonLabel || defaultNextButtonLabel,
              onClick: handleNextButtonClick,
            })
          : renderSubmitButton({
              errors,
              stepErrors,
              isLoading: isSubmitting,
              label: submitButtonLabel,
              onClick: submitForm,
            })
        }
      </StyledButtonContainer>
    </StyledContainer>
  );
};

export default Step;
